import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './profile-form.module.css';

import SuccessMessage from '../success-message/success-message';
import { useAppSelector } from '../../services/store/hooks';
import { ProfileFormData } from '../../types';
import { useUpdateProfileMutation } from '../../services/store/api/userApi';
import { useDateInputHandlers } from '../../hooks/useDateInputHandlers';

interface Props {
  fromShow?: 'rega' | 'profile';
}

const ProfileForm = ({ fromShow = 'rega' }: Props) => {
  const navigate = useNavigate();

  const { goals } = useAppSelector(state => state.dict);
  const { profile } = useAppSelector(state => state.user);

  const [
    updateProfile,
    { isLoading, isSuccess, isError, error },
  ] = useUpdateProfileMutation();

  const [formData, setFormData] = useState<ProfileFormData>({
    name: profile?.name ?? '',
    gender: profile?.gender ?? '',
    birthDate: profile?.birthdate ?? '',
    hideYear: profile?.hide_year ?? false,
    city: 'Москва/Московская область',
    relationshipStatus: profile?.relationship_status ?? '',
    hideRelationship: profile?.hide_relationship_status ?? false,
    goals: profile?.goals?.map(item => item.id) ?? [],
  });

  const [showSuccess, setShowSuccess] = useState(false);

  // 🔹 Хук для работы с датой
  const {
    nativeDateInputRef,
    dateInputValue,
    isFocused,

    handleCalendarIconClick,
    handleNativeDateChange,
    handleMaskedInputChange,
    handleFocus,
    handleBlur,
    handleKeyDown,
  } = useDateInputHandlers({
    value: formData.birthDate,
    onChange: (value) =>
      setFormData(prev => ({ ...prev, birthDate: value })),
  });

  const showMask = isFocused && !dateInputValue;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target;

    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [target.name]: target.checked,
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [target.name]: target.value,
    }));
  };

  const handleGenderChange = (gender: ProfileFormData['gender']) => {
    setFormData(prev => ({
      ...prev,
      gender,
    }));
  };

  const handleInterestToggle = (goalId: number) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.includes(goalId)
        ? prev.goals.filter(id => id !== goalId)
        : [...prev.goals, goalId],
    }));
  };

  const allGoalIds = goals.map(goal => goal.id);
  const isAllGoalsSelected = allGoalIds.length > 0
    && allGoalIds.every(goalId => formData.goals.includes(goalId));

  const handleAllGoalsToggle = () => {
    setFormData(prev => ({
      ...prev,
      goals: isAllGoalsSelected ? [] : allGoalIds,
    }));
  };

  const EyeIcon = ({ hidden }: { hidden: boolean }) => (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M2.5 12C4.6 7.8 7.8 5.7 12 5.7C16.2 5.7 19.4 7.8 21.5 12C19.4 16.2 16.2 18.3 12 18.3C7.8 18.3 4.6 16.2 2.5 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 15.2C13.7673 15.2 15.2 13.7673 15.2 12C15.2 10.2327 13.7673 8.8 12 8.8C10.2327 8.8 8.8 10.2327 8.8 12C8.8 13.7673 10.2327 15.2 12 15.2Z" stroke="currentColor" strokeWidth="1.8" />
      {hidden && <path d="M4 20L20 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />}
    </svg>
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await updateProfile(formData).unwrap();

      if (fromShow === 'rega') {
        navigate('/profile-interests');
      } else {
        setShowSuccess(true);
      }
    } catch {
      // ошибка отобразится ниже
    }
  };

  useEffect(() => {
    if (isSuccess && fromShow !== 'rega') {
      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, fromShow]);


  const hasValue = (value: string | undefined) => {
    return value !== undefined && value.trim() !== '';
  };

  return (
    <div className={styles.shell}>
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formBlock}>
          {/* Имя + Пол */}
          <div className={`${styles.formRow} ${styles.singleRow}`}>
            <div className={styles.formGroup}>
              <div className={styles.inputGroup}>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder=" "
                  autoComplete="given-name"
                />
                <label className={styles.floatingLabel}>Имя</label>
              </div>
            </div>

            <div className={styles.formGroup}>
              <div className={styles.segmentField}>
                <div className={styles.segmentLabel}>Пол</div>
                <div className={styles.segmentControl}>
                  <button
                    type="button"
                    className={`${styles.segmentButton} ${formData.gender === 'male' ? styles.segmentButtonActive : ''}`}
                    onClick={() => handleGenderChange('male')}
                  >
                    Мужской
                  </button>
                  <button
                    type="button"
                    className={`${styles.segmentButton} ${formData.gender === 'female' ? styles.segmentButtonActive : ''}`}
                    onClick={() => handleGenderChange('female')}
                  >
                    Женский
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Дата рождения */}
          <div className={`${styles.formRow} ${styles.withVisibilityRow}`}>
            <div className={styles.formGroup}>
              <div className={styles.dateInputWrapper}>
                <input
                  type="text"
                  className={`${styles.dateInput} ${showMask ? styles.masked : ''
                    }`}
                  placeholder={showMask ? 'дд.мм.гггг' : ' '}
                  value={dateInputValue}
                  onChange={handleMaskedInputChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  maxLength={10}
                  required
                  inputMode="numeric"
                />

                <span className={styles.datePlaceholder}>
                  Дата рождения
                </span>

                <button
                  type="button"
                  className={styles.dateIcon}
                  onClick={handleCalendarIconClick}
                  aria-label="Открыть календарь"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M7 3V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M17 3V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M4 9H20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M5 5.5H19C20.1046 5.5 21 6.39543 21 7.5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V7.5C3 6.39543 3.89543 5.5 5 5.5Z" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                </button>

                <input
                  ref={nativeDateInputRef}
                  type="date"
                  value={formData.birthDate}
                  onChange={handleNativeDateChange}
                  className={styles.hiddenDateInput}
                />
              </div>
            </div>

            <label className={styles.visibilityToggle} title={formData.hideYear ? 'Показывать год' : 'Скрыть год'}>
              <input
                type="checkbox"
                name="hideYear"
                checked={formData.hideYear}
                onChange={handleInputChange}
              />
              <span className={styles.visibilityIcon}>
                <EyeIcon hidden={formData.hideYear} />
              </span>
            </label>
          </div>

          {/* Регион */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <div className={styles.inputGroup}>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  readOnly
                  className={styles.input}
                  placeholder=" "
                />
                <label className={styles.floatingLabel}>Регион</label>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.formBlock}>
          {/* Семейное положение */}
          <div className={`${styles.formRow} ${styles.withVisibilityRow}`}>
            <div className={styles.formGroup}>
              <div className={styles.selectWrapper}>
                <select
                  name="relationshipStatus"
                  value={formData.relationshipStatus}
                  onChange={handleInputChange}
                  className={`${styles.select} ${hasValue(formData.relationshipStatus)
                    ? styles.hasValue
                    : ''
                    }`}
                >
                  <option value=""></option>
                  <option value="in_relationship">В отношениях</option>
                  <option value="single">Не в отношениях</option>
                </select>
                <label className={styles.floatingLabel}>
                  Семейное положение
                </label>
              </div>
            </div>

            <label className={styles.visibilityToggle} title={formData.hideRelationship ? 'Показывать статус' : 'Скрыть статус'}>
              <input
                type="checkbox"
                name="hideRelationship"
                checked={formData.hideRelationship}
                onChange={handleInputChange}
              />
              <span className={styles.visibilityIcon}>
                <EyeIcon hidden={formData.hideRelationship} />
              </span>
            </label>
          </div>
        </div>

        {/* Интересы */}
        <div className={styles.formBlock}>
          <div className={styles.sectionHeader}>
            <div className={styles.heading}>В поиске своих для:</div>
          </div>
          <div className={`${styles.formRow} ${styles.singleRow}`}>
            <div className={styles.formGroup}>
              <div className={styles.goalsContainer}>
                <button
                  type="button"
                  className={`${styles.goalButton} ${styles.allGoalsButton} ${isAllGoalsSelected ? styles.goalButtonActive : ''}`}
                  onClick={handleAllGoalsToggle}
                >
                  Интересно всё
                </button>
                {goals.map(goal => (
                  <button
                    key={goal.id}
                    type="button"
                    className={`${styles.goalButton} ${formData.goals.includes(goal.id)
                      ? styles.goalButtonActive
                      : ''
                      }`}
                    onClick={() => handleInterestToggle(goal.id)}
                  >
                    {goal.title}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className={styles.submitBar}>
          <button type="submit" disabled={isLoading} className={styles.submitButton}>
            {isLoading
              ? 'Сохранение...'
              : fromShow === 'rega'
                ? 'Продолжить'
                : 'Сохранить'}
          </button>
        </div>
      </form>

      {showSuccess && (
        <SuccessMessage>
          Профиль успешно сохранен!
        </SuccessMessage>
      )}

      {isError && (
        <div className="error-box">
          {(error as any)?.data?.message ?? 'Ошибка сохранения'}
        </div>
      )}
    </div>
  );
};

export default ProfileForm;
